import { IonIcon, IonImg, IonSpinner, useIonToast } from '@ionic/react';
import { IState } from 'appRedux/createStore';
import { ITrendsReport } from 'appRedux/models/casinoModels';
import { parseValueForTrends } from 'common/include';
import { heatBeanRatings } from 'config/HeatBeanRatings';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CasinoService, IRequestTrendsParam } from 'services/casino';
import {
  allInsightKeys,
  insightAccessList,
  InsightsItems,
  InsightValueEnum,
} from 'config/Insights';
import InsightModal, {
  ColdestRTP,
  ComparePlanSLJ,
  FindInsights,
  HottestRTP,
  Jackpot,
  JackpotWinSpin,
  LOTC,
  MoneyWon,
  NonJackpotWinSpin,
  NoOfJackpot,
  Played,
  Pop,
  Return,
  Spins,
  TrendsSectionInfo,
  Vol,
  WinRate,
  WinSpin,
} from 'components/InsightsPopup/InsightsPopup';
import clsx from 'clsx';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import { debounce } from 'lodash';
import { playerTypeEnum } from 'common/commonEnum';
import SectionHeader from './SectionHeader';
import { useHistory } from 'react-router';
import { arrowBackOutline, arrowForwardOutline } from 'ionicons/icons';
import { InsightValue } from 'common/types';
import GradientLineChart from './GradientLineChart';

interface TrendsComponentProps {
  slotNumber: number;
}

type IOrderedInsight = {
  key: string;
  label: string;
  includeInTrends: boolean;
  orderInTrends: number;
  playerTypeAccess?: playerTypeEnum[];
};

type GroupedRow = {
  key: string;
  label: string;
  playerTypeAccess?: playerTypeEnum[];
  item: ITrendsReport;
  isLocked: boolean;
};

const getHeatColorByNtile = (ntile: number | undefined) => {
  if (!ntile || !heatBeanRatings[ntile]) return 'rgba(0, 0, 0, 0.1)';
  return heatBeanRatings[ntile].color;
};

export const orderedInsights: IOrderedInsight[] = InsightsItems.map((tf) => {
  const matchingAccess = insightAccessList.find((fa) => fa.value === tf.value);

  if (!tf) return null;

  return {
    key: tf.value,
    label: tf.trendLabel,
    includeInTrends: tf.includeInTrends ?? true,
    orderInTrends: tf.orderInTrends ?? 0,
    ...(matchingAccess ? { playerTypeAccess: matchingAccess.playerTypeAccess } : {}),
  };
})
  .filter((item): item is NonNullable<typeof item> => !!item)
  .filter((item) => item.includeInTrends)
  .sort((a, b) => a.orderInTrends - b.orderInTrends);

// commenting out as we have above and cant find orderedInsightsItems in the code
// export const orderedInsights = orderedInsightsItems.map((tf) => {
//   const matchingAccess = insightAccessList.find((fa) => fa.value === tf.key);
//   return {
//     ...tf,
//     ...(matchingAccess ? { playerTypeAccess: matchingAccess.playerTypeAccess } : {}),
//   };
// });

const TrendsComponent: React.FC<TrendsComponentProps> = () => {
  const isLandscape = useSelector<IState>(
    (state) => state.app.persistedState.orientationState?.isOnlyPortrait
  ) as boolean;
  const ref = useRef<any>(null);
  const { activeSlotNumber } = useSlotDetailHistory();
  const history = useHistory();

  const [screenWidth, setScreenWidth] = useState(ref?.current?.clientWidth);
  const [showIonModal, setShowIonModal] = useState(false);
  const [activeInsight, setActiveInsight] = useState('');
  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;

  const insightFilter = '{1,2,3,4,5,7,8,12,13}';
  const orderByFilter = [1, 2, 3, 4, 5, 7, 8, 9, 12, 13];

  const [casinoParams, setCasinoParams] = useState<IRequestTrendsParam>({
    casinoFk: casinoId,
    insightFilter: InsightValueEnum.spins,
    slotNumber: 0,
  });

  const handleInputChange = (param: keyof IRequestTrendsParam, value: string | number) => {
    setCasinoParams((prevParams) => ({ ...prevParams, [param]: value }));
  };
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const headers = ['30 days', '7 days', '24 hrs', '8 hrs', '4 hrs', '1 hr'];

  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;

  const casinoSvc = new CasinoService();
  const [presentToast] = useIonToast();
  const [isTrendsLoading, setTrendsLoading] = useState(false);
  const [trendsData, setTrendsData] = useState<ITrendsReport[]>();

  const fetchTrendsGraph = (queryParams: IRequestTrendsParam) => {
    setTrendsLoading(true);
    casinoSvc.GetTrendsData(queryParams).subscribe({
      next(casinoResponse) {
        if (casinoResponse && casinoResponse?.DataSet) {
          const filteredTrends = casinoResponse?.DataSet.filter((trendsData) =>
            orderByFilter.includes(trendsData.order_by)
          );
          setTrendsData(filteredTrends);
        }
      },
      error(err) {
        presentToast({
          position: 'top',
          duration: 5000,
          color: 'danger',
          message: 'Failed to retrieve casino report.',
        }).catch(() => null);
      },
      complete() {
        setTrendsLoading(false);
      },
    });
  };

  useEffect(() => {
    if (ref?.current) {
      setScreenWidth(ref?.current?.clientWidth);
    }
  });

  useEffect(() => {
    if (ref?.current) {
      setTimeout(() => {
        setScreenWidth(ref?.current?.clientWidth);
      }, 1000);
    }
  }, [isLandscape]);

  useEffect(() => {
    handleInputChange('insightFilter', insightFilter);
  }, [insightFilter]);

  useEffect(() => {
    handleInputChange('slotNumber', activeSlotNumber);
  }, [activeSlotNumber]);

  const debouncedGetTrendsReport = useMemo(
    () =>
      debounce((casinoParams) => {
        fetchTrendsGraph(casinoParams);
      }, 500),
    [fetchTrendsGraph]
  );

  const firstRenderRef = useRef(true);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    debouncedGetTrendsReport(casinoParams);

    return () => {
      debouncedGetTrendsReport.cancel();
    };
  }, [casinoParams.slotNumber, casinoParams.insightFilter]);

  const groupByLocked = (
    insights: IOrderedInsight[],
    trendsData: ITrendsReport[] | undefined,
    isProUser: boolean
  ) => {
    const lockedRows: GroupedRow[] = [];
    const unlockedGroups: { locked: boolean; rows: GroupedRow[] }[] = [];
    let currentUnlocked: GroupedRow[] = [];

    insights.forEach(({ key, label, playerTypeAccess }) => {
      const item = trendsData?.find((trend) => trend.insight === key);
      if (!item) return;
      const playerType = isProUser ? playerTypeEnum.slotCheckPro : playerTypeEnum.slotCheck;
      const isLocked = !playerTypeAccess?.includes(playerType);

      if (isLocked) {
        lockedRows.push({ key, label, playerTypeAccess, item, isLocked });
        if (currentUnlocked.length) {
          unlockedGroups.push({ locked: false, rows: currentUnlocked });
          currentUnlocked = [];
        }
      } else {
        currentUnlocked.push({ key, label, playerTypeAccess, item, isLocked });
      }
    });

    if (currentUnlocked.length) {
      unlockedGroups.push({ locked: false, rows: currentUnlocked });
    }

    const groups = [...unlockedGroups];
    if (lockedRows.length) {
      groups.push({ locked: true, rows: lockedRows });
    }
    return groups;
  };

  const groupedRows = useMemo(
    () => groupByLocked(orderedInsights, trendsData, isProUser),
    [orderedInsights, trendsData, isProUser]
  );

  const scrollTrends = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 120;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className={clsx('trends-component', isLandscape ? 'landscape' : '')}>
      {isTrendsLoading ? (
        <div className="spinner-loading">
          <IonSpinner
            name="crescent"
            style={{
              width: '48px',
              height: '48px',
              color: '#4a90e2',
            }}
          />
        </div>
      ) : (
        <>
          {showIonModal && (
            <InsightModal isOpen={true} onDidDismiss={() => setShowIonModal(false)}>
              {activeInsight === InsightValueEnum.money_won && (
                <MoneyWon
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.return && (
                <Return
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.spins && (
                <Spins
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.pop && (
                <Pop
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.volatility_rating && (
                <Vol
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.jackpot_value && (
                <Jackpot
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.jackpots && (
                <NoOfJackpot
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.money_played && (
                <Played
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.avg_money_won_per_spin && (
                <WinSpin
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.non_jkpt_won_per_spin && (
                <NonJackpotWinSpin
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.jackpot_value_per_spin && (
                <JackpotWinSpin
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === InsightValueEnum.win_rate && (
                <WinRate
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === 'coldest_rtp' && (
                <ColdestRTP
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === 'hottest_rtp' && (
                <HottestRTP
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === 'trends_section_info' && (
                <TrendsSectionInfo
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === 'compare-plan-slj' && (
                <ComparePlanSLJ
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === 'find-insights' && (
                <FindInsights
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
              {activeInsight === 'lotc' && (
                <LOTC
                  onDismiss={() => {
                    setShowIonModal(false);
                  }}
                />
              )}
            </InsightModal>
          )}
          <SectionHeader title="Trends" showRow={false} iconName="trends-section-icon" />
          <div className="trend-scroll-controls">
            {!isLandscape && (
              <button className="scroll-arrow left" onClick={() => scrollTrends('left')}>
                <IonIcon icon={arrowBackOutline} />
              </button>
            )}
            <div className="trend-labels">
              <div className="label label-fixed"></div>
              {groupedRows.map((group) =>
                group.rows.map(({ key, label, isLocked }) => (
                  <div
                    key={key}
                    className={clsx('label label-fixed', { 'locked-label': isLocked })}
                    ref={ref}
                  >
                    {label}
                    <div className="label-icons">
                      <IonImg
                        src={'assets/icon/circle-info.svg'}
                        className="icon-info"
                        onClick={() => {
                          setActiveInsight(key);
                          setShowIonModal(true);
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="trend-scroll-area" ref={scrollRef}>
              <div className="trend-row trend-header-bg">
                <div className="trend-values">
                  {headers.map((header, index) => (
                    <div key={index} className="header-label" >
                      {header}
                    </div>
                  ))}
                </div>
              </div>
              {groupedRows.map((group) =>
                group.rows.map(({ key, label, playerTypeAccess, item, isLocked }) => {
                  const isVolatilityInsight = key === InsightValueEnum.volatility_rating;
                  const timeframeMap = [
                    { prefix: 'm', label: 'monthTrendValue' },
                    { prefix: 'w', label: 'weekTrendValue' },
                    { prefix: 'd', label: 'dayTrendValue' },
                    { prefix: 'h8', label: 'eightHoursTrendValue' },
                    { prefix: 'h4', label: 'fourHoursTrendValue' },
                    { prefix: 'h1', label: 'oneHourTrendValue' },
                  ] as const;

                  const rawValues = timeframeMap.map(({ prefix }) => ({
                    value: item[`${prefix}_insight_${isVolatilityInsight ? 'ntile' : 'value'}`],
                    ntile: item[`${prefix}_insight_ntile`],
                  }));

                  const values = rawValues.map((val) => ({
                    value: isLocked ? 'Blurred' : parseValueForTrends(val.value, item.insight),
                    ntile: val.ntile,
                  }));

                  const trendValues = Object.fromEntries(
                    timeframeMap.map(({ prefix, label }) => [
                      label,
                      isVolatilityInsight
                        ? item[`${prefix}_insight_ntile`]
                        : {
                          value: item[`${prefix}_insight_value`],
                          ntile: item[`${prefix}_insight_ntile`],
                        },
                    ])
                  );
                  const visibleInsights: InsightValue[] = allInsightKeys.filter(
                    (i) => i == 'return'
                  );
          
                  return (
                    <div className="trend-row" key={key}>
                      {!isLocked && visibleInsights.length > 0 && (
                        <GradientLineChart
                          key={key}
                          trendValues={trendValues}
                          keyName={key}
                          screenWidth={screenWidth}
                          isLandscape={isLandscape}
                          insightType={key}
                          visibleInsights={visibleInsights}
                          postionLeft={4}
                        />
                      )}
                      <div className="trend-values">
                        {values.map((value, idx) => (
                          <div
                            key={idx}
                            className={`value ${value.value === 'Blurred' ? 'blurred' : ''}`}
                          >
                            {value.value !== 'Blurred' && (
                              <span
                                className="ntile-color"
                                style={{ backgroundColor: getHeatColorByNtile(value.ntile) }}
                              />
                            )}
                            {value.value === 'Blurred' ? (
                              <span className="blurred-text">*******</span>
                            ) : (
                              value.value
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
              {groupedRows.some((group) => group.locked) && (
                <div className="blur-overlay-global">
                  <div className="unlock-title">Unlock PRO Trends</div>
                  <button
                    className="unlock-btn"
                    onClick={() =>
                      history.push('/auth/stripe', {
                        previousPage: '/tabs/home',
                      })
                    }
                  >
                    Upgrade now
                  </button>
                </div>
              )}
            </div>
            {!isLandscape && (
              <button className="scroll-arrow right" onClick={() => scrollTrends('right')}>
                <IonIcon icon={arrowForwardOutline} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TrendsComponent;
